import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdClose, MdSend } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { TemplatesFallback, WhatsappList, WhatsappTemplate, extractPlaceholders } from './types';
import TemplatesFallbackNotice from './TemplatesFallbackNotice';

interface Recipient {
    _id: string;
    name?: string;
    surname?: string;
    email: string;
    phone?: string;
}

interface Resolution {
    found: Recipient[];
    notFound: string[];
    noPhone: string[];
}

interface SendResult {
    sent: number;
    failed: number;
    total: number;
    failedEmails: string[];
    notFound: string[];
    noPhone: string[];
}

const parseEmails = (text: string) =>
    [...new Set(text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e.includes('@')))];

const inputClass =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white';

export default function SendMessageModal({
    lists,
    onClose,
}: {
    lists: WhatsappList[];
    onClose: () => void;
}) {
    const [mode, setMode] = useState<'emails' | 'list'>('emails');
    const [emailsText, setEmailsText] = useState('');
    const [resolution, setResolution] = useState<Resolution | null>(null);
    const [resolving, setResolving] = useState(false);
    const [listId, setListId] = useState(lists[0]?._id || '');

    const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [templatesFallback, setTemplatesFallback] = useState<TemplatesFallback>({ fallback: false });
    const [templateSid, setTemplateSid] = useState('');
    const [variableValues, setVariableValues] = useState<Record<string, string>>({});

    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<SendResult | null>(null);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${CONFIG.apiUrl}/admin/whatsapp-templates`, { headers });
                setTemplates(res.data.templates.filter((t: WhatsappTemplate) => t.approval?.status === 'approved'));
                setTemplatesFallback({ fallback: !!res.data.fallback, fallbackReason: res.data.fallbackReason });
            } catch (e) {
                console.error(e);
            } finally {
                setLoadingTemplates(false);
            }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resolve typed emails against users (debounced)
    const emails = useMemo(() => parseEmails(emailsText), [emailsText]);
    useEffect(() => {
        if (mode !== 'emails' || emails.length === 0) {
            setResolution(null);
            return;
        }
        const timeout = setTimeout(async () => {
            try {
                setResolving(true);
                const res = await axios.post(`${CONFIG.apiUrl}/admin/whatsapp-recipients/resolve`, { emails }, { headers });
                setResolution(res.data);
            } catch (e) {
                console.error(e);
            } finally {
                setResolving(false);
            }
        }, 500);
        return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emails, mode]);

    const sortedLists = useMemo(
        () => [...lists].sort((a, b) => b.userIds.length - a.userIds.length),
        [lists]
    );
    const selectedList = lists.find((l) => l._id === listId);
    const template = templates.find((t) => t.sid === templateSid) || null;
    const placeholders = template ? extractPlaceholders(template.body) : [];

    const recipientCount = mode === 'emails' ? resolution?.found.length ?? 0 : selectedList?.userIds.length ?? 0;
    const missingVariables = placeholders.some((k) => !variableValues[k]?.trim());
    const canSend = !!template && recipientCount > 0 && !missingVariables && !sending && !resolving;

    const selectTemplate = (sid: string) => {
        setTemplateSid(sid);
        const tpl = templates.find((t) => t.sid === sid);
        const initial: Record<string, string> = {};
        if (tpl) extractPlaceholders(tpl.body).forEach((k) => { initial[k] = tpl.variables?.[k] ?? ''; });
        setVariableValues(initial);
    };

    const preview = template
        ? template.body.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => variableValues[k] || `{{${k}}}`)
        : '';

    const handleSend = async () => {
        if (!template || !canSend) return;
        const target = mode === 'emails' ? `${recipientCount} utilisateur(s)` : `la liste "${selectedList?.name}"`;
        if (!window.confirm(`Envoyer le template "${template.friendlyName}" à ${target} ?`)) return;
        try {
            setSending(true);
            const payload = {
                contentSid: template.sid,
                contentVariables: variableValues,
                ...(mode === 'emails' ? { emails } : { listId }),
            };
            const res = await axios.post(`${CONFIG.apiUrl}/admin/whatsapp-send`, payload, { headers });
            setResult(res.data);
        } catch (e: any) {
            console.error(e);
            alert(e?.response?.data?.message || 'Échec de l\'envoi');
        } finally {
            setSending(false);
        }
    };

    const tabClass = (active: boolean) =>
        `flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            active
                ? 'bg-white text-navy-700 shadow-sm dark:bg-navy-600 dark:text-white'
                : 'text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white'
        }`;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="relative z-10 m-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-navy-800">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
                    <h2 className="flex items-center gap-2 text-lg font-bold text-navy-700 dark:text-white">
                        <FaWhatsapp className="text-green-500" /> Envoyer un message WhatsApp
                    </h2>
                    <button onClick={onClose} className="rounded p-1 text-gray-400 hover:text-gray-600">
                        <MdClose className="text-xl" />
                    </button>
                </div>

                {result ? (
                    <div className="space-y-3 overflow-y-auto px-6 py-5">
                        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm dark:border-green-800 dark:bg-green-900/20">
                            <div className="font-medium text-green-700 dark:text-green-300">Envoi terminé</div>
                            <div className="mt-1 text-green-600 dark:text-green-400">
                                ✓ {result.sent} envoyé(s)
                                {result.failed > 0 && <span className="ml-2 text-red-500">✗ {result.failed} échoué(s)</span>}
                            </div>
                        </div>
                        {[
                            { label: 'Échec Twilio', items: result.failedEmails },
                            { label: 'Utilisateur introuvable', items: result.notFound },
                            { label: 'Sans numéro valide', items: result.noPhone },
                        ]
                            .filter((g) => g.items?.length > 0)
                            .map((g) => (
                                <div key={g.label}>
                                    <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                        {g.label} ({g.items.length})
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300">{g.items.join(', ')}</div>
                                </div>
                            ))}
                        <div className="flex justify-end">
                            <button
                                onClick={onClose}
                                className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="space-y-5 overflow-y-auto px-6 py-5">
                            {/* Recipients */}
                            <div>
                                <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">Destinataires</div>
                                <div className="mb-3 flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-navy-900">
                                    <button onClick={() => setMode('emails')} className={tabClass(mode === 'emails')}>
                                        Par email
                                    </button>
                                    <button onClick={() => setMode('list')} className={tabClass(mode === 'list')}>
                                        Liste
                                    </button>
                                </div>

                                {mode === 'emails' ? (
                                    <>
                                        <textarea
                                            value={emailsText}
                                            onChange={(e) => setEmailsText(e.target.value)}
                                            rows={3}
                                            autoFocus
                                            placeholder="jean@exemple.com, paul@exemple.com…  (séparés par virgule, espace ou retour à la ligne)"
                                            className={inputClass}
                                        />
                                        {resolving && <div className="mt-2 text-xs text-gray-400">Vérification...</div>}
                                        {!resolving && resolution && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {resolution.found.map((u) => (
                                                    <span
                                                        key={u._id}
                                                        title={`${u.email} · ${u.phone}`}
                                                        className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                                    >
                                                        ✓ {[u.name, u.surname].filter(Boolean).join(' ') || u.email}
                                                    </span>
                                                ))}
                                                {resolution.noPhone.map((email) => (
                                                    <span
                                                        key={email}
                                                        title="Pas de numéro de téléphone valide"
                                                        className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                                    >
                                                        ☎ {email}
                                                    </span>
                                                ))}
                                                {resolution.notFound.map((email) => (
                                                    <span
                                                        key={email}
                                                        title="Aucun utilisateur avec cet email"
                                                        className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                                    >
                                                        ✗ {email}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : lists.length === 0 ? (
                                    <div className="text-sm text-gray-400">Aucune liste</div>
                                ) : (
                                    <select value={listId} onChange={(e) => setListId(e.target.value)} className={inputClass}>
                                        {sortedLists.map((l) => (
                                            <option key={l._id} value={l._id}>
                                                {l.name} ({l.userIds.length} membre{l.userIds.length !== 1 ? 's' : ''})
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Template */}
                            <div>
                                <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">Template</div>
                                {templatesFallback.fallback && (
                                    <TemplatesFallbackNotice reason={templatesFallback.fallbackReason} className="mb-2" />
                                )}
                                {loadingTemplates ? (
                                    <div className="text-sm text-gray-400">Chargement des templates...</div>
                                ) : templates.length === 0 ? (
                                    <div className="text-sm text-gray-400">Aucun template approuvé par WhatsApp.</div>
                                ) : (
                                    <select value={templateSid} onChange={(e) => selectTemplate(e.target.value)} className={inputClass}>
                                        <option value="">Choisir un template…</option>
                                        {templates.map((t) => (
                                            <option key={t.sid} value={t.sid}>
                                                {t.friendlyName}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                {template && placeholders.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                            Variables — utilisez {'{prenom}'} ou {'{nom}'} pour personnaliser
                                        </div>
                                        {placeholders.map((k) => (
                                            <div key={k} className="flex items-center gap-2">
                                                <span className="w-12 shrink-0 font-mono text-xs text-gray-500">{`{{${k}}}`}</span>
                                                <input
                                                    type="text"
                                                    value={variableValues[k] ?? ''}
                                                    onChange={(e) => setVariableValues((prev) => ({ ...prev, [k]: e.target.value }))}
                                                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {template && (
                                    <div className="mt-3 rounded-lg bg-[#efeae2] p-3 dark:bg-navy-900">
                                        <div className="max-w-[90%] whitespace-pre-wrap rounded-lg rounded-tl-none bg-white p-3 text-sm text-gray-800 shadow-sm dark:bg-navy-700 dark:text-gray-100">
                                            {preview}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-navy-600">
                            <button
                                onClick={onClose}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSend}
                                disabled={!canSend}
                                className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <MdSend />
                                {sending ? 'Envoi en cours...' : `Envoyer à ${recipientCount} destinataire${recipientCount !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
